import React, { useState } from "react";
import { useModal } from "../../contexts/ModalContext";
import { useNavigate } from "react-router-dom";
import { REDIRECT_FE_URLS } from '../../utils/Constants';
import { useLoader } from "../../contexts/LoaderContext";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import api from "../../services/api";

import { 
  TextField, 
  Button, 
  Typography, 
  Box, 
  Container, 
  Paper, 
  FormControlLabel, 
  Checkbox, 
  Link, 
  FormHelperText 
} from "@mui/material";

const CompleteOnboarding = () => {
  const navigate = useNavigate();
  const { openSuccessModal, openErrorModal, closeModal } = useModal();
  const { showLoader, hideLoader } = useLoader();
  const [formData, setFormData] = useState({
    company_name: "",
    phone_number: "",
    tnc_consent: false
  });

  const [errorMessage, setErrorMessage] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Special handling for phone_number field
    if (name === 'phone_number') {
      // Remove all non-digit characters
      const digitsOnly = value.replace(/\D/g, '');
      
      // Limit to 10 digits
      const limitedValue = digitsOnly.slice(0, 10);
      
      setFormData({
        ...formData,
        [name]: limitedValue,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
    
    // Clear specific error when the input is modified
    setErrorMessage({
      ...errorMessage,
      [name]: "",
    });
  };
  
  const handleFormSubmit = (e) => {
    e.preventDefault();
    // Validate the form
    const validationErrors = {};
    if (!formData.company_name){
        validationErrors.company_name = "Company Name is required";
    } else if(formData.company_name.length < 3) {
      validationErrors.company_name = "Company Name must be at least 3 characters long.";
    }

    if (!formData.phone_number) {
      validationErrors.phone_number = "Mobile number is required";
    } else if (!/^\d{10}$/.test(formData.phone_number.replace(/\D/g, ''))) {
      validationErrors.phone_number = "Please enter a valid 10-digit mobile number";
    }

    if(!formData.tnc_consent) {
      validationErrors.tnc_consent = "You must agree to the Terms & Privacy Policy";
    }

    // If validation errors exist, show them and prevent submission
    if (Object.keys(validationErrors).length > 0) {
      setErrorMessage(validationErrors);
      return;
    }

    showLoader("Submitting..."); // Show loader

    // Call the API to complete onboarding
    completeOnboardingApiRequest(formData);
  };

  const completeOnboardingApiRequest = async (data) => {
    try {
      const response = await api.post(`complete/onboarding/`, data);
      
      hideLoader(); // Hide loader before showing the modal
      
      if (response.status >= 200 && response.status < 300) {
        // Success case
        const result = response.data;
        console.log(result);
        
        // Define the callback function that will be executed when OK is clicked
        const handleOnConfirm = () => {
          // The modal will be automatically closed by the context
          
          // Handle navigation based on the response
          if (result && result.redirect_url) {
            // If redirect_url exists in the response, navigate to it
            navigate(result.redirect_url);
          } else if (result && result.account_id) {
            // If account_id exists but no redirect_url, navigate to account status
            navigate(`/account/${result.account_id}/status`);
          } else {
            // Fallback if no redirect information is provided
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            navigate('/auth/login');
          }
        };
        
        // Pass the callback to openSuccessModal
        openSuccessModal(
          "Onboarding Completed Successfully", 
          "Your account has been created and is under review. You will receive an email once your account is verified.",
          handleOnConfirm
        );
        
      } else {
        // Handle error responses
        throw new Error("Request failed with status: " + response.status);
      }
    } catch (error) {
      hideLoader();
      // Handle different types of errors
      if (error.response) {
        // The request was made and the server responded with an error status
        const errorData = error.response.data;
        
        // Handle field-specific errors
        if (errorData.company_name) {
          setErrorMessage(prevErrors => ({
            ...prevErrors,
            company_name: Array.isArray(errorData.company_name) 
              ? errorData.company_name[0] 
              : errorData.company_name
          }));
        }
        
        if (errorData.phone_number) {
          setErrorMessage(prevErrors => ({
            ...prevErrors,
            phone_number: Array.isArray(errorData.phone_number) 
              ? errorData.phone_number[0] 
              : errorData.phone_number
          }));
        }
        
        // Handle validation errors
        if (errorData.errors) {
          setErrorMessage(prevErrors => ({
            ...prevErrors,
            ...errorData.errors
          }));
        }
        
        openErrorModal("Onboarding Failed", errorData.message || "An error occurred during onboarding");
      } else if (error.request) {
        // The request was made but no response was received
        openErrorModal("Onboarding Failed", "No response received from server. Please check your connection.");
      } else {
        // Something happened in setting up the request
        openErrorModal("Onboarding Failed", error.message || "An unexpected error occurred");
      }
    }
  };

  const handleCheckboxChange = (e) => {
    setFormData({
      ...formData,
      tnc_consent: e.target.checked
    });
    // Clear error when checkbox is checked
    if (e.target.checked) {
      setErrorMessage({
        ...errorMessage,
        tnc_consent: ""
      });
    }
  };

  return (
    <Paper
      elevation={2}
      sx={{
        width: '100%',
        p: { xs: 3, sm: 3 },
        borderRadius: 2,
        backgroundColor: 'white',
      }}
    >
        <Box sx={{ mt: 3, mb: 4 }}>
          <Typography variant="h4" component="h1" align="center" gutterBottom>
            Complete Onboarding
          </Typography>
          <Typography variant="subtitle1" align="center" color="textSecondary">
            Start Your 14-Days Free Trial Now!
          </Typography>
        </Box>
        
          <Box component="form" noValidate onSubmit={handleFormSubmit} sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="company_name"
              label="Company Name"
              name="company_name"
              autoComplete="organization"
              value={formData.company_name}
              onChange={handleChange}
              error={!!errorMessage.company_name}
              helperText={errorMessage.company_name}
              autoFocus
            />
            
            <TextField
              margin="normal"
              required
              fullWidth
              id="phone_number"
              label="Mobile Number"
              name="phone_number"
              type="text"
              autoComplete="tel"
              value={formData.phone_number}
              onChange={handleChange}
              error={!!errorMessage.phone_number}
              helperText={errorMessage.phone_number}
              inputProps={{
                pattern: '[0-9]*'
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: errorMessage.phone_number ? '#d32f2f' : undefined,
                  },
                  '&.Mui-error fieldset': {
                    borderColor: '#d32f2f !important',
                    borderWidth: '2px',
                  },
                },
                '& .Mui-error .MuiOutlinedInput-notchedOutline': {
                  backgroundColor: 'rgba(211, 47, 47, 0.04)',
                  borderColor: '#d32f2f !important',
                },
              }}
            />
            
            <Box sx={{ mt: 2 }} size={{sx:"12", md:"12", lg:"12"}}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.tnc_consent}
                    onChange={handleCheckboxChange}
                    name="tnc_consent"
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2">
                    I agree to the{" "}
                    <Link href="#" target="_blank" underline="hover">
                      Terms of Service
                    </Link>{" "}
                    and{" "}
                    <Link href="#" target="_blank" underline="hover">
                      Privacy Policy
                    </Link>
                  </Typography>
                }
              />
              {errorMessage.tnc_consent && (
                <FormHelperText error>{errorMessage.tnc_consent}</FormHelperText>
              )}
            </Box>
            
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              size="large"
              sx={{ mt: 3, mb: 2, py: 1.5 }}
            >
              Submit
            </Button>
          </Box>
    </Paper>
  );
};

export default CompleteOnboarding;
